import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext(null);

// Terminology mapping for operative vs executive mode
const terminology = {
  operative: {
    killSwitch: 'Kill Switch',
    zombieTrial: 'Zombie Trial',
    deployAgent: 'Deploy Agent',
    commandCenter: 'Command Center',
    wasteScore: 'Waste Score',
    scan: 'Re-Scan',
    startScan: 'Start Free Scan',
    scanNow: 'Scan Now',
    cancel: 'KILL',
    pause: 'FREEZE',
    resume: 'REVIVE',
    negotiate: 'NEGOTIATE',
    threat: 'Threat Letter',
    resurrect: 'Resurrect',
    appName: 'SUB-ZERO',
    appTagline: 'GHOST AUDITOR v2.0',
    login: 'Access Terminal',
    register: 'Deploy Your Agent',
    signIn: 'Infiltrate',
    signUp: 'Activate Agent',
    dashboard: 'Command Center',
    settings: 'Configurations',
    savings: 'Total Recovered',
    monthlySpend: 'Monthly Bleed',
    potential: 'Potential Recovery',
    trials: 'Zombie Trials',
    trialsKilled: 'Trials Killed',
    share: 'Share Intel',
    trackTrial: 'Track Trial',
    noSubs: 'No targets detected',
    heroTitle: ['Your', 'Subscriptions', 'Are ', 'Robbing', ' You Right Now.'],
    heroSubtitle: 'Stop The Silent Bleed — Track, Control & Optimize All Recurring Payments.',
    heroDescription: 'The average person bleeds $412/year on forgotten subscriptions, untracked renewals, and zombie free trials. Sub-Zero is the AI agent that hunts them all down.',
    ctaPrimary: 'Deploy Your Agent — Free',
    ctaSecondary: 'Watch Kill Switch Demo',
    featureTag: 'ZOMBIE KILLER',
  },
  executive: {
    killSwitch: 'Cancel Service',
    zombieTrial: 'Expiring Trial',
    deployAgent: 'Get Started',
    commandCenter: 'Dashboard',
    wasteScore: 'Savings Opportunity',
    scan: 'Refresh',
    startScan: 'Scan My Subscriptions',
    scanNow: 'Scan Now',
    cancel: 'Cancel',
    pause: 'Pause',
    resume: 'Resume',
    negotiate: 'Negotiate',
    threat: 'Cancellation Letter',
    resurrect: 'Resubscribe',
    appName: 'Sub-Zero',
    appTagline: 'Smart Subscription Manager',
    login: 'Sign In',
    register: 'Create Account',
    signIn: 'Sign In',
    signUp: 'Get Started',
    dashboard: 'Dashboard',
    settings: 'Settings',
    savings: 'Total Saved',
    monthlySpend: 'Monthly Spend',
    potential: 'Potential Savings',
    trials: 'Free Trials',
    trialsKilled: 'Trials Cancelled',
    share: 'Share',
    trackTrial: 'Track Trial',
    noSubs: 'No subscriptions found',
    heroTitle: ['Take Control', 'of Every', 'Subscription.'],
    heroSubtitle: 'Track, manage, and optimize all your recurring payments in one beautiful place.',
    heroDescription: 'The average person spends $412/year on subscriptions they barely use. Sub-Zero finds them, tracks them, and helps you save.',
    ctaPrimary: 'Start Saving — Free',
    ctaSecondary: 'See How It Works',
    featureTag: 'SMART SAVINGS',
  }
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('subzero-theme') || 'dark';
    }
    return 'dark';
  });

  const [mode, setMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('subzero-mode') || 'operative';
    }
    return 'operative';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.remove('operative', 'executive');

    root.classList.add(theme);
    root.classList.add(mode === 'executive' ? 'executive' : 'operative');

    localStorage.setItem('subzero-theme', theme);
    localStorage.setItem('subzero-mode', mode);
  }, [theme, mode]);

  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  }, []);

  const toggleMode = useCallback(() => {
    setMode(prev => {
      const next = prev === 'operative' ? 'executive' : 'operative';
      return next;
    });
  }, []);

  const t = useCallback((key) => {
    return terminology[mode]?.[key] || terminology.operative[key] || key;
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, mode, setMode, toggleMode, t }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
